import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import nothingToShowImg from '../../assets/datagrid-nothing-to-show.png';
import { GridColDef, GridColumnHeaderParams, GridPaginationModel } from '@mui/x-data-grid';
import UserAvatar from '../../components/UserAvatar';
import { useNavigate } from 'react-router-dom';
import NothingToShow from '../../components/nothingToShow';
import CustomSearchBox from '../../components/CustomSearchBox';
import EmptyAvatar from '../../assets/avatar_circular.png';
import { Typography, Button } from '@mui/material';
import { getRapports } from '../../services/rapports';
import {
  RapportProps,
  RapportResponse,
  CreatePatientFormData,
} from '../../utils/constants';
import { useRapportsStore } from '../../store/rapportsStore';
import CustomDataGrid from '../../components/CustomDataGrid';
import CreatePatientModal from './components/CreatePatientModal';
import PatientSuccessModal from './components/PatientSuccessModal';
import { createPatient } from '../../services/patients';
import { useAlertStore } from '../../store/alertStore';
import { useDebounce } from '../../utils/useDebounce';

const RapportsInitialScreen = () => {
  const [searchText, setSearchText] = useState<string>('');
  const debouncedSearchText = useDebounce(searchText, 500);
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [successModalOpen, setSuccessModalOpen] = useState<boolean>(false);
  const [createdPatient, setCreatedPatient] =
    useState<CreatePatientFormData | null>(null);
  const [rapports, setRapports] = useState<RapportProps[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 6,
  });

  const navigate = useNavigate();
  const { setRapports: setRapportsStore } = useRapportsStore();
  const { setAlert } = useAlertStore();

  // Función para cargar los rapports con paginación
  const fetchRapports = useCallback(async (page: number, pageSize: number, search?: string) => {
    setIsLoading(true);
    try {
      const offset = page * pageSize;
      const response = await getRapports({ 
        offset, 
        limit: pageSize,
        search: search && search.trim().length > 0 ? search.trim() : undefined,
      });
      
      if (response && response.items) {
        const mappedRapports = response.items.map((rapport: RapportResponse) => {
          const { id, patient } = rapport;
          return {
            id,
            lastName: patient.lastName,
            firstName: patient.firstName,
            email: patient.email,
            profilePictureUrl: patient.profilePictureUrl,
          };
        });
        
        setRapports(mappedRapports);
        setRapportsStore(mappedRapports);
        setRowCount(response.pagination.count);
      }
    } catch (error) {
      console.error('Error fetching rapports:', error);
    } finally {
      setIsLoading(false);
    }
  }, [setRapportsStore]);

  // Función para refrescar la lista de rapports (vuelve a página 1)
  const refreshRapports = useCallback(async () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    await fetchRapports(0, paginationModel.pageSize, debouncedSearchText);
  }, [fetchRapports, paginationModel.pageSize, debouncedSearchText]);

  // Resetear a página 0 cuando cambia el searchText (sin debounce para mejor UX)
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [searchText]);

  // Cargar datos cuando cambia la paginación o el debouncedSearchText
  useEffect(() => {
    fetchRapports(paginationModel.page, paginationModel.pageSize, debouncedSearchText);
  }, [paginationModel.page, paginationModel.pageSize, debouncedSearchText, fetchRapports]);

  // Handler para cambios en la paginación
  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  // Función para manejar la creación de pacientes
  const handleCreatePatient = async (data: CreatePatientFormData) => {
    try {
      const response = await createPatient(data);

      if (response.status === 201) {
        setCreatedPatient(data);
        setSuccessModalOpen(true);
        setAlert({
          isOpen: true,
          message: 'Paciente creado exitosamente',
          severity: 'success',
        });
        // Refrescar la lista de rapports
        await refreshRapports();
      } else {
        throw new Error(
          `Error al crear el paciente: ${response.response.data.message}`,
        );
      }
    } catch (error: any) {
      console.error('Error creating patient:', error);

      // Manejar errores específicos
      let errorMessage = 'Ha ocurrido un error al crear el paciente';
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setAlert({
        isOpen: true,
        message: errorMessage,
        severity: 'error',
      });

      throw error; // Re-throw para que el modal maneje el estado de loading
    }
  };

  // Función para abrir el modal de creación
  const handleOpenCreateModal = () => {
    setCreateModalOpen(true);
  };

  // Función para cerrar el modal de creación
  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
  };

  // Función para cerrar el modal de éxito
  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
    setCreatedPatient(null);
  };

  // Función para crear otro paciente desde el modal de éxito
  const handleCreateAnother = () => {
    setSuccessModalOpen(false);
    setCreatedPatient(null);
    setCreateModalOpen(true);
  };

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: 'Paciente',
      renderHeader: (params: GridColumnHeaderParams): any => {
        return (
          <Typography className='pl-4' fontWeight='bold'>
            Paciente
          </Typography>
        );
      },
      editable: false,
      renderCell: (cellValues): any => {
        return (
          <div className='flex flex-row items-center pl-4'>
            <UserAvatar
              imageUrl={
                cellValues.row.profilePictureUrl !== undefined
                  ? cellValues.row.profilePictureUrl
                  : EmptyAvatar
              }
              size={44}
              altText={'IMG'}
            />
            <Typography className='pl-8'>
              {cellValues.row.firstName} {cellValues.row.lastName}
            </Typography>
          </div>
        );
      },
      flex: 1,
      sortable: false,
    },
    {
      field: 'email',
      headerName: 'Email',
      renderHeader: (params: GridColumnHeaderParams): any => {
        return (
          <Typography className='pl-4' fontWeight='bold'>
            Email
          </Typography>
        );
      },
      renderCell: (cellValues): any => {
        return (
          <div className='flex flex-row items-center'>
            <Typography className='pl-4'>{cellValues.row.email}</Typography>
          </div>
        );
      },
      editable: false,
      flex: 1,
      sortable: false,
    },
    {
      field: 'hypertext',
      headerName: '',
      editable: false,
      renderHeader: (params: GridColumnHeaderParams): any => {
        return <div className='pl-4'></div>;
      },
      renderCell: (cellValues): any => {
        return (
          <Typography
            onClick={() =>
              navigate(`/rapports/${cellValues.row.id}`, {
                replace: true,
                state: {
                  rapportDetails: cellValues.row,
                  id: cellValues.row.id,
                },
              })
            }
            className='tracking-wider text-center text-sm text-aqua-intense underline cursor-pointer'
            fontWeight='bold'
          >
            Ver rapport
          </Typography>
        );
      },
      flex: 0.25,
      sortable: false,
    },
  ];

  return (
    <Layout>
      <div className='flex flex-col w-full h-full pb-8'>
        <div className='flex w-full items-center'>
          <div className='flex w-1/5 justify-start pl-2 pr-2'>
            <Typography
              className={`p-10 pl-12 text-pitch-black tracking-wider`}
              fontSize={28}
              fontWeight='bold'
            >
              Rapports
            </Typography>
          </div>
          <div className='flex w-4/5 justify-end pl-2 pr-2 gap-4 items-center'>
            <Button
              color='secondary'
              variant='contained'
              className='w-[200px] h-[48px]'
              onClick={handleOpenCreateModal}
              sx={{
                backgroundColor: '#DAE86D',
                color: '#333',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#c8d661',
                },
              }}
            >
              Nuevo paciente
            </Button>
            <CustomSearchBox
              onChange={(value) => setSearchText(value)}
              value={searchText}
            />
          </div>
        </div>
        <div className='bg-white mx-14'>
          <div
            className={`flex mr-4 ${
              rapports.length !== 0 ? 'h-[495px]' : 'pb-12'
            }`}
            style={{ minHeight: '495px' }}
          >
            {rapports.length === 0 ? (
              <NothingToShow
                nothingImg={nothingToShowImg}
                altText={'sin data de paciente'}
                nothingText={'No tenés nada que ver hasta el momento...'}
              />
            ) : (
              <CustomDataGrid
                rows={rapports}
                rowHeight={64}
                columns={columns}
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[6]}
                loading={isLoading}
                disableRowSelectionOnClick={false}
                slotProps={{
                  pagination: {
                    labelRowsPerPage: '',
                    rowsPerPageOptions: [],
                  },
                }}
              />
            )}
          </div>
        </div>

        {/* Modal de Creación de Paciente */}
        <CreatePatientModal
          open={createModalOpen}
          onClose={handleCloseCreateModal}
          onSubmit={handleCreatePatient}
          onPatientCreated={(patient) => {
            console.log('Patient created:', patient);
          }}
        />

        {/* Modal de Éxito */}
        <PatientSuccessModal
          open={successModalOpen}
          onClose={handleCloseSuccessModal}
          patientName={
            createdPatient
              ? `${createdPatient.firstName} ${createdPatient.lastName}`
              : undefined
          }
          patientEmail={createdPatient?.email}
          onCreateAnother={handleCreateAnother}
        />
      </div>
    </Layout>
  );
};

export default RapportsInitialScreen;
